import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { ToastVariant } from '../../components/toast';
import { PlanError } from '../../entity/api/plan';
import type {
  CreateSeoStrategyKeywordReq,
  GetSeoStrategyByBrandIdRes,
  LinkSeoStrategyKeywordToContentReq,
  LinkSeoStrategyKeywordToContentRes,
  SaveSeoStrategyReq,
  SearchSeoStrategyKeywordsReq,
  UpdateActionByIdReq,
  UpdateSeoStrategyKeywordReq,
} from '../../entity/api/routes/seo-strategy';
import { SeoStrategyStatus } from '../../entity/api/seo-strategy';
import { ForbiddenError } from '../../error';
import { convertError } from '../../error/convert';
import { trackError } from '../../error/tracking';
import { captureException } from '../../logger/capture-exception';
import {
  createSeoStrategyKeyword,
  getActionPlanByBrandId,
  getSeoStrategyByBrandId,
  linkSeoStrategyKeyword,
  regenerateSeoStrategy,
  removeSeoStrategyKeyword,
  saveSeoStrategy,
  searchSeoStrategyKeywords,
  updateActionById,
  updateSeoStrategyKeyword,
} from '../../sdk/seo-strategy';
import { useToast } from '../../shared/contexts/toast-context';
import { PostHogEvents, capturePostHogEvent } from '../posthog';
import { invalidateOrganizationBilling } from './index';

// ============ QUERY KEYS ============

export const seoStrategyKeys = {
  all: ['seo-strategy'] as const,
  byBrandId: function (brandId: string) {
    return [...seoStrategyKeys.all, 'brand', brandId] as const;
  },
  keywords: function (brandId: string, query?: SearchSeoStrategyKeywordsReq['query']) {
    return ['seo-strategy', 'keywords', brandId, query] as [
      'seo-strategy',
      'keywords',
      string,
      SearchSeoStrategyKeywordsReq['query'] | undefined,
    ];
  },
  actionPlan: function (brandId: string) {
    return ['seo-strategy', 'action-plan', brandId] as const;
  },
};

// ============ QUERIES ============

export function useSeoStrategyByBrandId(brandId: string | undefined | null) {
  return useQuery({
    queryKey: seoStrategyKeys.byBrandId(brandId ?? ''),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getSeoStrategyByBrandId(brandId);
      return response.data;
    },
    enabled: brandId != null && brandId !== '',
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============ CACHE HELPERS ============

/**
 * Optimistically marks the SEO strategy as Pending so the UI transitions to
 * the loading state immediately while the save + AI regeneration runs in the
 * background (which can take a long time). Returns the previous cache snapshot
 * so callers can roll back if the mutation fails.
 */
export function useSetSeoStrategyStatusPending() {
  const queryClient = useQueryClient();

  return function (brandId: string): GetSeoStrategyByBrandIdRes['data'] | undefined {
    const queryKey = seoStrategyKeys.byBrandId(brandId);
    const previousData = queryClient.getQueryData<GetSeoStrategyByBrandIdRes['data']>(queryKey);

    queryClient.setQueryData<GetSeoStrategyByBrandIdRes['data']>(queryKey, function (prev) {
      if (prev?.strategy == null) {
        return prev;
      }
      return { ...prev, strategy: { ...prev.strategy, status: SeoStrategyStatus.Pending } };
    });

    return previousData;
  };
}

/**
 * Restores the SEO strategy cache to a previous snapshot.
 * Used to roll back an optimistic Pending status update when a mutation fails.
 */
export function useRollbackSeoStrategy() {
  const queryClient = useQueryClient();

  return function (
    brandId: string,
    previousData: GetSeoStrategyByBrandIdRes['data'] | undefined
  ): void {
    queryClient.setQueryData(seoStrategyKeys.byBrandId(brandId), previousData);
  };
}

// ============ MUTATIONS ============

export function useSaveSeoStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      brandId,
      data,
    }: {
      brandId: string;
      data: SaveSeoStrategyReq['body'];
    }) {
      const response = await saveSeoStrategy(brandId, data);
      return { brandId, socketResponse: response };
    },
    onSuccess: function ({ brandId, socketResponse }) {
      if (socketResponse.result != null) {
        queryClient.setQueryData<GetSeoStrategyByBrandIdRes['data']>(
          seoStrategyKeys.byBrandId(brandId),
          socketResponse.result
        );
      } else {
        void queryClient.invalidateQueries({
          queryKey: seoStrategyKeys.byBrandId(brandId),
        });
      }
    },
  });
}

/**
 * Specialized mutation for the refresh audit modal. Handles the full flow:
 * optimistic status update, API call, cache sync, error handling with rollback,
 * and success analytics. Mutation-level callbacks ensure they fire even if
 * the component unmounts before the request completes.
 */
export function useSaveRefreshAudit(brandId: string, organizationId: string) {
  const queryClient = useQueryClient();
  const addToast = useToast();
  const navigate = useNavigate();

  return useMutation<
    { brandId: string; socketResponse: Awaited<ReturnType<typeof saveSeoStrategy>> },
    Error,
    SaveSeoStrategyReq['body'],
    { previousData: GetSeoStrategyByBrandIdRes['data'] | undefined }
  >({
    mutationFn: async function (data: SaveSeoStrategyReq['body']) {
      const response = await saveSeoStrategy(brandId, data);
      return { brandId, socketResponse: response };
    },
    onMutate: function () {
      const queryKey = seoStrategyKeys.byBrandId(brandId);
      const previousData = queryClient.getQueryData<GetSeoStrategyByBrandIdRes['data']>(queryKey);

      queryClient.setQueryData<GetSeoStrategyByBrandIdRes['data']>(queryKey, function (prev) {
        if (prev?.strategy == null) {
          return prev;
        }
        return { ...prev, strategy: { ...prev.strategy, status: SeoStrategyStatus.Pending } };
      });

      return { previousData };
    },
    onSuccess: function ({ brandId: mutationBrandId, socketResponse }) {
      capturePostHogEvent(PostHogEvents.SEO_AUDIT_RUN, {
        urlAudited: socketResponse.result?.strategy?.websiteUrl,
      });

      invalidateOrganizationBilling(queryClient, organizationId);

      if (socketResponse.result != null) {
        queryClient.setQueryData<GetSeoStrategyByBrandIdRes['data']>(
          seoStrategyKeys.byBrandId(mutationBrandId),
          socketResponse.result
        );
      } else {
        void queryClient.invalidateQueries({
          queryKey: seoStrategyKeys.byBrandId(mutationBrandId),
        });
      }
    },
    onError: function (err, _variables, context) {
      const previousData = context?.previousData ?? undefined;
      if (previousData != null) {
        queryClient.setQueryData(seoStrategyKeys.byBrandId(brandId), previousData);
      }

      const handledErr = convertError(err);
      if (handledErr instanceof ForbiddenError) {
        if ((handledErr.originalMessage as PlanError) === PlanError.InsufficientCredits) {
          addToast({
            variant: ToastVariant.Error,
            title: 'Insufficient credits',
            id: Date.now(),
          });
          void navigate({
            to: '/app/settings/organization/$id/plan',
            params: { id: organizationId },
          });
          return;
        }

        addToast({
          variant: ToastVariant.Error,
          title: handledErr.originalMessage?.toString() ?? 'An error occurred',
          id: Date.now(),
        });
        return;
      }

      captureException(
        err,
        {
          file: 'services/storage/seo-strategy-queries.ts',
          method: 'useSaveRefreshAudit',
          infoMessage: 'Failed to save and regenerate SEO strategy',
        },
        trackError,
        addToast
      );
    },
  });
}

export function useRegenerateSeoStrategy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (brandId: string) {
      const response = await regenerateSeoStrategy(brandId);
      return { brandId, socketResponse: response };
    },
    onSuccess: function ({ brandId, socketResponse }) {
      if (socketResponse.result != null) {
        queryClient.setQueryData<GetSeoStrategyByBrandIdRes['data']>(
          seoStrategyKeys.byBrandId(brandId),
          socketResponse.result
        );
      } else {
        void queryClient.invalidateQueries({
          queryKey: seoStrategyKeys.byBrandId(brandId),
        });
      }
    },
  });
}

// ============ KEYWORDS QUERIES ============

export function useSeoStrategyKeywords(
  brandId: string | undefined | null,
  query?: SearchSeoStrategyKeywordsReq['query']
) {
  return useQuery({
    queryKey: seoStrategyKeys.keywords(brandId ?? '', query),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      return searchSeoStrategyKeywords(brandId, query);
    },
    enabled: brandId != null && brandId !== '',
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateSeoStrategyKeyword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      brandId,
      data,
    }: {
      brandId: string;
      data: CreateSeoStrategyKeywordReq['body'];
    }) {
      const response = await createSeoStrategyKeyword(brandId, data);
      return { brandId, response };
    },
    onSuccess: function ({ brandId }) {
      void queryClient.invalidateQueries({
        queryKey: seoStrategyKeys.keywords(brandId),
      });
    },
  });
}

export function useUpdateSeoStrategyKeyword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      brandId,
      keywordId,
      data,
    }: {
      brandId: string;
      keywordId: string;
      data: UpdateSeoStrategyKeywordReq['body'];
    }) {
      await updateSeoStrategyKeyword(brandId, keywordId, data);
      return { brandId };
    },
    onSuccess: function ({ brandId }) {
      void queryClient.invalidateQueries({
        queryKey: seoStrategyKeys.keywords(brandId),
      });
    },
  });
}

export function useRemoveSeoStrategyKeyword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ brandId, keywordId }: { brandId: string; keywordId: string }) {
      await removeSeoStrategyKeyword(brandId, keywordId);
      return { brandId };
    },
    onSuccess: function ({ brandId }) {
      void queryClient.invalidateQueries({
        queryKey: ['seo-strategy', 'keywords', brandId],
      });
    },
  });
}

export function useLinkSeoStrategyKeyword() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      brandId,
      data,
    }: {
      brandId: string;
      data: LinkSeoStrategyKeywordToContentReq['body'];
    }): Promise<{ brandId: string; result: LinkSeoStrategyKeywordToContentRes }> {
      const result = await linkSeoStrategyKeyword(brandId, data);
      return { brandId, result };
    },
    onSuccess: function ({ brandId, result }) {
      if (result.linked) {
        void queryClient.invalidateQueries({
          queryKey: ['seo-strategy', 'keywords', brandId],
        });
      }
    },
  });
}

// ============ ACTION PLAN QUERIES ============

export function useActionPlanByBrandId(brandId: string | undefined | null) {
  return useQuery({
    queryKey: seoStrategyKeys.actionPlan(brandId ?? ''),
    queryFn: async function () {
      if (brandId == null) {
        throw new Error('Brand ID is required');
      }
      const response = await getActionPlanByBrandId(brandId);
      return response.data;
    },
    enabled: brandId != null && brandId !== '',
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdateActionById() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({
      brandId,
      actionId,
      data,
    }: {
      brandId: string;
      actionId: string;
      data: UpdateActionByIdReq['body'];
    }) {
      await updateActionById(brandId, actionId, data);
      return { brandId };
    },
    onSuccess: function ({ brandId }) {
      void queryClient.invalidateQueries({
        queryKey: seoStrategyKeys.actionPlan(brandId),
      });
    },
  });
}
